import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Fade } from "@mui/material";
import { UserContext } from "../contexts/userContext";
import { fetchData } from "../utils/dataProvider";
import {
  AUTHENTICATION_PATH,
  ENTITY_ROUTES,
  GET,
  IMPERSONATION_STATE,
  TOKEN,
} from "../utils/constants";
import { ISaveUserData } from "../interfaces/userContext";
import { clearLocalData, storeData } from "../utils/storage";

type ImpersonateStatus = "loading" | "success" | "error";

const Impersonate = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { saveUserData, logout, currentUser } = useContext(UserContext);
  const [status, setStatus] = useState<ImpersonateStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const hasStartedRef = useRef(false);

  const handleImpersonate = async () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (!token) {
      navigate(AUTHENTICATION_PATH, { replace: true });
      return;
    }

    if (currentUser?.entity) {
      logout();
    }

    clearLocalData();

    try {
      storeData(TOKEN, token);
      storeData(IMPERSONATION_STATE, JSON.stringify({ isImpersonating: true }));

      const { user } = await fetchData({
        url: "/users/getUserByToken",
        method: GET,
      });

      saveUserData({ token, user } as ISaveUserData);

      setStatus("success");

      const { entity } = user;
      navigate(ENTITY_ROUTES[entity], { replace: true });
    } catch {
      clearLocalData();
      setStatus("error");
      setError("Token inválido o expirado");
      setTimeout(() => {
        navigate(AUTHENTICATION_PATH, { replace: true });
      }, 2000);
    }
  };

  useEffect(() => {
    handleImpersonate();
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      sx={{ bgcolor: "background.default" }}
    >
      <Fade in timeout={300}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          {status === "error" ? (
            <Typography color="error" variant="h6">
              {error}
            </Typography>
          ) : (
            <>
              <CircularProgress size={80} thickness={4} />
              <Typography variant="body1" color="text.secondary">
                Iniciando sesión...
              </Typography>
            </>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

export default Impersonate;
